import { Kana, NumberIdentity, Version } from "@ascend-ltd/logic-helpers";
import { z } from "zod/v4";
import { OfficeId } from "../office";
import { RoleId } from "../role/role-entity";
import { SegmentId } from "../segment";
import { TenantId } from "../tenant";
// UserId のブランド型
const UserIdSchema = NumberIdentity.schema.brand();
export const UserId = {
    schema: UserIdSchema,
    of: (id) => UserId.schema.parse(id),
};
// AccountName のブランド型
const AccountNameSchema = z
    .string()
    .min(8)
    .max(32)
    .regex(/^[a-zA-Z0-9.-]*$/, "アカウント名に使用できない文字が含まれています")
    .describe("アカウント名")
    .brand();
export const AccountName = {
    schema: AccountNameSchema,
    of: (accountName) => AccountName.schema.parse(accountName),
};
// UserName のブランド型
const UserNameSchema = z
    .object({
    familyName: z.string().min(1).max(32),
    givenName: z.string().min(1).max(32),
    familyNameKana: Kana.schema.and(z.string().min(1).max(32)),
    givenNameKana: Kana.schema.and(z.string().min(1).max(32)),
})
    .describe("ユーザー名")
    .brand();
export const UserName = {
    schema: UserNameSchema,
    of: (name) => UserName.schema.parse(name),
};
// UserRole の定義
const UserRoleTypeList = [
    "ascend:admin",
    "ascend:manager",
    "ascend:staff",
    "tenant:manager",
    "tenant:staff",
];
const UserRoleNames = {
    "ascend:admin": "アセンドアドミン",
    "ascend:manager": "アセンドマネジャー",
    "ascend:staff": "アセンドスタッフ",
    "tenant:manager": "管理者",
    "tenant:staff": "一般利用者",
};
const UserRoleSchema = z
    .enum(UserRoleTypeList)
    .describe("ユーザー権限")
    .brand();
export const UserRole = {
    schema: UserRoleSchema,
    of: (role) => UserRole.schema.parse(role),
    userRoleNames: (role) => UserRoleNames[role],
    // 静的ファクトリメソッド
    ascendAdmin: () => UserRole.of("ascend:admin"),
    ascendManager: () => UserRole.of("ascend:manager"),
    ascendStaff: () => UserRole.of("ascend:staff"),
    tenantManager: () => UserRole.of("tenant:manager"),
    tenantStaff: () => UserRole.of("tenant:staff"),
    // ヘルパーメソッド
    isAscend: (role) => role.startsWith("ascend:"),
    isAscendAdmin: (role) => role === "ascend:admin",
    isAscendManager: (role) => role === "ascend:manager",
    isAscendStaff: (role) => role === "ascend:staff",
    isTenant: (role) => role.startsWith("tenant:"),
    isTenantManager: (role) => role === "tenant:manager",
    isTenantStaff: (role) => role === "tenant:staff",
};
// UserEntity のスキーマ定義
const UserEntitySchema = z
    .object({
    id: UserId.schema,
    tenantId: TenantId.schema,
    officeId: OfficeId.schema.optional(),
    accountName: AccountName.schema,
    userRole: UserRole.schema,
    roleId: RoleId.schema.optional(),
    officeIds: z.array(OfficeId.schema),
    segmentIds: z.array(SegmentId.schema),
    isDisabled: z.boolean().optional(),
    disabledAt: z.date().optional(),
    version: Version.schema,
})
    .extend(UserName.schema.shape);
const create = (args) => {
    const values = {
        id: NumberIdentity.generate(),
        ...args,
        version: Version.initial(),
        isDisabled: undefined,
        disabledAt: undefined,
    };
    return UserEntitySchema.parse(values);
};
const updateBasic = (entity, updates) => {
    const newEntity = {
        ...entity,
        ...(updates.officeId !== undefined && {
            officeId: updates.officeId
                ? OfficeId.schema.parse(updates.officeId)
                : undefined,
        }),
        ...(updates.officeIds && {
            officeIds: updates.officeIds.map((id) => OfficeId.schema.parse(id)),
        }),
        ...(updates.segmentIds && {
            segmentIds: updates.segmentIds.map((id) => SegmentId.schema.parse(id)),
        }),
        familyName: updates.familyName,
        familyNameKana: updates.familyNameKana,
        givenName: updates.givenName,
        givenNameKana: updates.givenNameKana,
    };
    return UserEntitySchema.parse(newEntity);
};
const updateRole = (entity, userRole, roleId) => {
    const newEntity = {
        ...entity,
        userRole: UserRole.of(userRole),
        roleId: roleId ? RoleId.schema.parse(roleId) : undefined,
    };
    return UserEntitySchema.parse(newEntity);
};
const disable = (entity) => {
    const newEntity = {
        ...entity,
        isDisabled: true,
        disabledAt: new Date(),
    };
    return UserEntitySchema.parse(newEntity);
};
export const UserEntity = {
    schema: UserEntitySchema,
    reconstruct: (args) => UserEntity.schema.parse(args),
    create,
    updateBasic,
    updateRole,
    disable,
};
//# sourceMappingURL=user-entity.js.map